import { UI_LOCALES, type UiLocale } from '@acs/shared';
import en from './locales/en.json';

/**
 * The languages the PORTAL is rendered in — the owner's own reading language
 * (doc 20 D1).
 *
 * Not to be confused with `Language` from `@acs/shared`, which is what the
 * ASSISTANT speaks to callers: a different axis, owned by the business rather
 * than the user, and deliberately not extended by this module. A Polish owner
 * running a salon in Berlin wants a Polish dashboard and a German assistant.
 */
// Declared in `@acs/shared` since doc 88: auth emails are rendered server-side
// in the reader's portal locale, and the callable contract that carries it is a
// shared schema (ADR-0008).
export { UI_LOCALES, type UiLocale } from '@acs/shared';

export const DEFAULT_LOCALE: UiLocale = 'en';

/**
 * Each language named in ITSELF (its endonym), never translated.
 *
 * Someone who has landed in a language they cannot read is scanning this list
 * for the word THEY would write for their own language — "Polski", not
 * "Polish" and not "Polnisch". Translating these would make the menu unusable
 * for exactly the person who needs it.
 */
export const LOCALE_ENDONYM: Record<UiLocale, string> = {
  en: 'English',
  pl: 'Polski',
  de: 'Deutsch',
  fr: 'Français',
  uk: 'Українська',
};

/** BCP-47 tag for `Intl.*` and `<html lang>`. */
export const LOCALE_TAG: Record<UiLocale, string> = {
  en: 'en-GB',
  pl: 'pl-PL',
  de: 'de-DE',
  fr: 'fr-FR',
  uk: 'uk-UA',
};

/** The English catalogue is the source of truth: it types every key in the app. */
export type Catalog = Record<string, string>;

/** CLDR plural categories, as `Intl.PluralRules` reports them. */
type PluralSuffix = '_zero' | '_one' | '_two' | '_few' | '_many' | '_other';

/**
 * Every key `t()` accepts.
 *
 * Plural entries are stored one-per-form (`x_one`, `x_few`, …) but are CALLED
 * by their base (`t('x', { count })`) — the form is chosen at runtime from the
 * count and the locale, so the base is what a call site can know. Mapping the
 * suffixed keys back to their base therefore both admits the call and rejects
 * `t('x_one')`, which would hard-code English's idea of how many forms exist.
 */
type BaseKey<K extends string> = K extends `${infer B}${PluralSuffix}` ? B : K;

export type TranslationKey = BaseKey<Extract<keyof typeof en, string>>;

/**
 * English is bundled; the rest are fetched on demand (doc 20 D3).
 *
 * Measured, not guessed: the five catalogues are 230 KB raw / 63 KB gzipped
 * together — a quarter of the whole app's transfer, of which any one owner
 * reads a fifth. Ukrainian alone is 60 KB raw, because Cyrillic costs two
 * bytes a character in UTF-8. English stays static because it is the fallback
 * every lookup ends at (`lookup`), so it has to be there synchronously.
 */
export const EN: Catalog = en;

const LOADERS: Record<Exclude<UiLocale, 'en'>, () => Promise<{ default: Catalog }>> = {
  pl: () => import('./locales/pl.json'),
  de: () => import('./locales/de.json'),
  fr: () => import('./locales/fr.json'),
  uk: () => import('./locales/uk.json'),
};

/** Catalogues already in memory. English is there from the start. */
const loaded = new Map<UiLocale, Catalog>([['en', en]]);

export function loadedCatalog(locale: UiLocale): Catalog | undefined {
  return loaded.get(locale);
}

/**
 * Fetch a catalogue, memoised. Resolves immediately for anything already in
 * memory, so switching back to a language costs nothing.
 *
 * A failed chunk fetch resolves to English rather than rejecting: a flaky
 * network should degrade the wording, never blank the page (R9).
 */
export async function loadCatalog(locale: UiLocale): Promise<Catalog> {
  const have = loaded.get(locale);
  if (have) return have;
  try {
    const mod = await LOADERS[locale as Exclude<UiLocale, 'en'>]();
    loaded.set(locale, mod.default);
    return mod.default;
  } catch {
    return en;
  }
}

export const STORAGE_KEY = 'tc-locale';

export function isUiLocale(value: unknown): value is UiLocale {
  return typeof value === 'string' && (UI_LOCALES as readonly string[]).includes(value);
}

/**
 * Which language to open in, before the owner has ever chosen (doc 20 D6):
 * their stored choice → the first browser language whose PRIMARY SUBTAG we ship
 * (`pl-PL` → `pl`) → English.
 *
 * Matching on the subtag rather than the whole tag is the point: we ship one
 * Polish, and `pl-PL`, `pl` and a hypothetical `pl-SK` must all find it.
 */
export function detectLocale(
  stored: string | null,
  preferred: readonly string[] = [],
): UiLocale {
  if (isUiLocale(stored)) return stored;
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split('-')[0];
    if (isUiLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

/** The stored choice, or the browser's, resolved against `window`. */
export function readInitialLocale(): UiLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* private mode — fall through to browser languages */
  }
  return detectLocale(stored, navigator.languages ?? [navigator.language]);
}
